import { createSlice } from "@reduxjs/toolkit";
import { isSilentRequestAbortError } from "../../utils/apiErrorMessage";

const makeColumnState = () => ({
  items: [],
  nextCursor: null,
  page: 1,
  total: 0,
  hasMore: false,
  initialLoading: false,
  loadingMore: false,
  error: null,
});

const initialState = {
  boards: {},
  /** Column metadata per board (label, category, theme) — from bootstrap API for global Kanban. */
  boardColumnDefs: {},
};

function ensureBoardColumns(state, boardKey, columnKeys = []) {
  if (!state.boards[boardKey]) {
    state.boards[boardKey] = {};
  }
  columnKeys.forEach((key) => {
    if (!state.boards[boardKey][key]) {
      state.boards[boardKey][key] = makeColumnState();
    }
  });
}

export const kanbanColumnsSlice = createSlice({
  name: "kanbanColumns",
  initialState,
  reducers: {
    initBoardColumns: (state, action) => {
      const { boardKey, columnKeys } = action.payload;
      state.boards[boardKey] = {};
      ensureBoardColumns(state, boardKey, columnKeys);
    },
    resetColumn: (state, action) => {
      const { boardKey, columnKey } = action.payload;
      if (state.boards[boardKey]) {
        state.boards[boardKey][columnKey] = makeColumnState();
      }
    },
  },
  extraReducers: (builder) => {
    // Use action type strings to avoid a circular import:
    //   kanbanColumnsSlice → kanbanColumnsThunk → ApiBase → StoreRef → store → kanbanColumnsSlice
    const PREFIX = "kanbanColumns/fetchPage";
    const BOOTSTRAP_PREFIX = "kanbanColumns/bootstrap";
    builder
      .addMatcher(
        (action) => action.type === `${BOOTSTRAP_PREFIX}/pending`,
        (state, action) => {
          const { boardKey, columns = [] } = action.meta.arg;
          const defs = columns.length > 0 ? columns : (state.boardColumnDefs[boardKey] ?? []);
          const columnKeys = defs.map((col) => col.key);
          if (!columnKeys.length) return;
          state.boards[boardKey] = {};
          ensureBoardColumns(state, boardKey, columnKeys);
          columnKeys.forEach((columnKey) => {
            const col = state.boards[boardKey][columnKey];
            col.initialLoading = true;
            col.loadingMore = false;
            col.error = null;
            col.items = [];
            col.nextCursor = null;
            col.page = 1;
            col.total = 0;
            col.hasMore = false;
          });
        }
      )
      .addMatcher(
        (action) => action.type === `${BOOTSTRAP_PREFIX}/fulfilled`,
        (state, action) => {
          const { boardKey, columns = [], columnDefs = [] } = action.payload;
          if (columnDefs.length) {
            state.boardColumnDefs[boardKey] = columnDefs;
          }
          const columnKeys = columns.map((col) => col.columnKey);
          state.boards[boardKey] = {};
          ensureBoardColumns(state, boardKey, columnKeys);
          columns.forEach(({ columnKey, items, nextCursor, hasMore, total }) => {
            const col = state.boards[boardKey]?.[columnKey];
            if (!col) return;
            col.items = items;
            col.nextCursor = nextCursor;
            col.page = 1;
            col.total = total ?? 0;
            col.hasMore = hasMore;
            col.initialLoading = false;
            col.loadingMore = false;
            col.error = null;
          });
        }
      )
      .addMatcher(
        (action) => action.type === `${BOOTSTRAP_PREFIX}/rejected`,
        (state, action) => {
          const { boardKey, columns = [] } = action.meta.arg;
          const errorMsg = action.payload ?? action.error?.message ?? "Failed to load";
          const silent = action.meta?.aborted || isSilentRequestAbortError(errorMsg);
          if (silent) return;
          const defs = columns.length > 0 ? columns : (state.boardColumnDefs[boardKey] ?? []);
          ensureBoardColumns(
            state,
            boardKey,
            defs.map((col) => col.key)
          );
          defs.forEach(({ key }) => {
            const col = state.boards[boardKey]?.[key];
            if (!col) return;
            col.initialLoading = false;
            col.loadingMore = false;
            col.error = errorMsg;
          });
        }
      );
    builder
      .addMatcher(
        (action) => action.type === `${PREFIX}/pending`,
        (state, action) => {
          const { boardKey, columnKey, isLoadMore } = action.meta.arg;
          const col = state.boards[boardKey]?.[columnKey];
          if (!col) return;
          if (isLoadMore) {
            col.loadingMore = true;
          } else {
            col.initialLoading = true;
          }
          col.error = null;
        }
      )
      .addMatcher(
        (action) => action.type === `${PREFIX}/fulfilled`,
        (state, action) => {
          const { boardKey, columnKey, isLoadMore, page: requestedPage } = action.meta.arg;
          const col = state.boards[boardKey]?.[columnKey];
          if (!col) return;

          const { items, nextCursor, hasMore, total } = action.payload;

          if (isLoadMore) {
            const existingIds = new Set(col.items.map((i) => i.id));
            const newItems = items.filter((i) => !existingIds.has(i.id));
            col.items = [...col.items, ...newItems];
            col.loadingMore = false;
            col.page = requestedPage ?? (col.page ?? 1) + 1;
          } else {
            col.items = items;
            col.initialLoading = false;
            col.page = 1;
          }

          col.nextCursor = nextCursor;
          col.total = total ?? col.total;
          col.hasMore = hasMore;
          col.error = null;
        }
      )
      .addMatcher(
        (action) => action.type === `${PREFIX}/rejected`,
        (state, action) => {
          const { boardKey, columnKey, isLoadMore } = action.meta.arg;
          const errorMsg = action.payload ?? action.error?.message ?? "Failed to load";
          const silent = action.meta?.aborted || isSilentRequestAbortError(errorMsg);
          if (silent) return;
          const col = state.boards[boardKey]?.[columnKey];
          if (!col) return;
          if (isLoadMore) {
            col.loadingMore = false;
          } else {
            col.initialLoading = false;
          }
          col.error = errorMsg;
        }
      );
  },
});

export const { initBoardColumns, resetColumn } = kanbanColumnsSlice.actions;

export const selectKanbanBoard = (state, boardKey) => state.kanbanColumns.boards[boardKey] ?? null;

export const selectKanbanBoardColumnDefs = (state, boardKey) =>
  state.kanbanColumns.boardColumnDefs[boardKey] ?? [];

export const selectKanbanColumn = (state, boardKey, columnKey) =>
  state.kanbanColumns.boards[boardKey]?.[columnKey] ?? null;

export const selectKanbanBoardAllItems = (state, boardKey) => {
  const board = state.kanbanColumns.boards[boardKey];
  if (!board) return [];
  return Object.values(board).flatMap((col) => col.items);
};

export default kanbanColumnsSlice.reducer;
